import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiEye, FiEyeOff, FiKey, FiSave, FiUser, FiX } from 'react-icons/fi';
import api from '../../services/api';
import useUser from '../../hooks/useUser';
import { CRUD_UI } from '../crudUi';

function UserProfileMenu() {
  const navigate = useNavigate();
  const { user, fullName, email, fetchUser, logout } = useUser();
  const [open, setOpen] = useState(false);
  const [modalType, setModalType] = useState<'profile' | 'security' | null>(null);
  const [saving, setSaving] = useState(false);
  const [profileForm, setProfileForm] = useState({
    displayName: fullName || '',
    email: email || '',
  });
  const [profileErrors, setProfileErrors] = useState<{ displayName?: string; email?: string }>({});
  const [profileTouched, setProfileTouched] = useState<{ displayName?: boolean; email?: boolean }>({});
  const [securityForm, setSecurityForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [securityErrors, setSecurityErrors] = useState<{ currentPassword?: string; newPassword?: string; confirmPassword?: string }>({});
  const [securityTouched, setSecurityTouched] = useState<{ currentPassword?: boolean; newPassword?: boolean; confirmPassword?: boolean }>({});
  const containerRef = useRef<HTMLDivElement | null>(null);

  const headerDisplayName = fullName || email || 'User';
  const avatarLetter = headerDisplayName.charAt(0).toUpperCase();

  useEffect(() => {
    if (!modalType) {
      setProfileForm({
        displayName: fullName || '',
        email: email || '',
      });
    }
  }, [fullName, email, modalType]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleProfile = () => {
    setOpen(false);
    setModalType('profile');
  };

  const handleSecurity = () => {
    setOpen(false);
    setModalType('security');
  };

  const handleLogout = () => {
    setOpen(false);
    logout();
    navigate('/login', { replace: true });
  };

  const closeModal = () => {
    setModalType(null);
    setSaving(false);
    setProfileForm({
      displayName: fullName || '',
      email: email || '',
    });
    setProfileErrors({});
    setProfileTouched({});
    setSecurityForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    setShowCurrentPassword(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
    setSecurityErrors({});
    setSecurityTouched({});
  };

  const validateEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  const validateProfileField = (field: 'displayName' | 'email', value: string) => {
    if (field === 'displayName') {
      if (!value.trim()) return 'Display name là bắt buộc';
      if (value.trim().length < 3) return 'Display name tối thiểu 3 ký tự';
      if (value.trim().length > 50) return 'Display name tối đa 50 ký tự';
      return '';
    }
    if (field === 'email') {
      if (!value.trim()) return 'Email là bắt buộc';
      if (!validateEmail(value.trim())) return 'Email không hợp lệ';
      return '';
    }
    return '';
  };

  const validateProfileForm = () => {
    const nextErrors: { displayName?: string; email?: string } = {};
    const displayNameError = validateProfileField('displayName', profileForm.displayName);
    const emailError = validateProfileField('email', profileForm.email);
    if (displayNameError) nextErrors.displayName = displayNameError;
    if (emailError) nextErrors.email = emailError;
    return nextErrors;
  };

  const validateSecurityField = (field: 'currentPassword' | 'newPassword' | 'confirmPassword', value: string, draft = securityForm) => {
    if (field === 'currentPassword') {
      if (!value) return 'Mật khẩu hiện tại là bắt buộc';
      return '';
    }
    if (field === 'newPassword') {
      if (!value) return 'Mật khẩu mới là bắt buộc';
      if (value.length < 6) return 'Mật khẩu mới tối thiểu 6 ký tự';
      return '';
    }
    if (field === 'confirmPassword') {
      if (!value) return 'Xác nhận mật khẩu là bắt buộc';
      if (value !== draft.newPassword) return 'Mật khẩu xác nhận không khớp';
      return '';
    }
    return '';
  };

  const validateSecurityForm = () => {
    const nextErrors: { currentPassword?: string; newPassword?: string; confirmPassword?: string } = {};
    const currentPasswordError = validateSecurityField('currentPassword', securityForm.currentPassword);
    const newPasswordError = validateSecurityField('newPassword', securityForm.newPassword);
    const confirmPasswordError = validateSecurityField('confirmPassword', securityForm.confirmPassword, securityForm);
    if (currentPasswordError) nextErrors.currentPassword = currentPasswordError;
    if (newPasswordError) nextErrors.newPassword = newPasswordError;
    if (confirmPasswordError) nextErrors.confirmPassword = confirmPasswordError;
    return nextErrors;
  };

  const handleSaveProfile = async () => {
    const errors = validateProfileForm();
    setProfileErrors(errors);
    setProfileTouched({ displayName: true, email: true });
    if (Object.keys(errors).length > 0) {
      toast.error('Vui lòng kiểm tra lại thông tin cá nhân');
      return;
    }

    try {
      setSaving(true);
      await api.put('/auth/profile', {
        displayName: profileForm.displayName,
        email: profileForm.email,
      });
      await fetchUser();
      toast.success('Cập nhật thông tin thành công');
      closeModal();
    } catch (error: any) {
      toast.error(error?.response?.data?.error?.message || 'Không thể cập nhật thông tin');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    const errors = validateSecurityForm();
    setSecurityErrors(errors);
    setSecurityTouched({ currentPassword: true, newPassword: true, confirmPassword: true });
    if (Object.keys(errors).length > 0) {
      toast.error('Vui lòng kiểm tra lại mật khẩu');
      return;
    }

    try {
      setSaving(true);
      await api.post('/auth/security/change-password', {
        currentPassword: securityForm.currentPassword,
        newPassword: securityForm.newPassword,
      });
      toast.success('Đổi mật khẩu thành công');
      closeModal();
    } catch (error: any) {
      toast.error(error?.response?.data?.error?.message || 'Không thể đổi mật khẩu');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        className="flex items-center gap-2 rounded-full px-2 py-1.5 text-sm text-slate-700 transition-colors hover:bg-slate-100"
        onClick={() => setOpen((prev) => !prev)}
      >
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-200 text-xs font-semibold text-slate-700">
          {avatarLetter}
        </span>
        <span className="max-w-[180px] truncate font-medium">{headerDisplayName}</span>
        <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4 text-slate-500">
          <path
            fillRule="evenodd"
            d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.51a.75.75 0 01-1.08 0l-4.25-4.51a.75.75 0 01.02-1.06z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {open && (
        <div className="absolute right-0 z-40 mt-2 w-52 rounded-xl border border-slate-200 bg-white p-1 shadow-lg">
          <button
            type="button"
            className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-slate-700 transition-colors hover:bg-slate-100"
            onClick={handleProfile}
          >
            <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4 text-slate-500">
              <path d="M10 2a4 4 0 100 8 4 4 0 000-8zM3 16a7 7 0 1114 0H3z" />
            </svg>
            <span>Thông tin cá nhân</span>
          </button>

          <button
            type="button"
            className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-slate-700 transition-colors hover:bg-slate-100"
            onClick={handleSecurity}
          >
            <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4 text-slate-500">
              <path
                fillRule="evenodd"
                d="M10 1a3 3 0 00-3 3v2H6a2 2 0 00-2 2v7a2 2 0 002 2h8a2 2 0 002-2V8a2 2 0 00-2-2h-1V4a3 3 0 00-3-3zM8.5 6V4a1.5 1.5 0 113 0v2h-3z"
                clipRule="evenodd"
              />
            </svg>
            <span>Đổi mật khẩu</span>
          </button>

          <button
            type="button"
            className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-red-600 transition-colors hover:bg-red-50"
            onClick={handleLogout}
          >
            <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4 text-red-500">
              <path
                fillRule="evenodd"
                d="M3 4a2 2 0 012-2h5a1 1 0 110 2H5v12h5a1 1 0 110 2H5a2 2 0 01-2-2V4zm10.293 3.293a1 1 0 011.414 0l3 3a1 1 0 010 1.414l-3 3a1 1 0 01-1.414-1.414L14.586 12H8a1 1 0 110-2h6.586l-1.293-1.293a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
            <span>Đăng xuất</span>
          </button>
        </div>
      )}

      {modalType && createPortal(
        <div className={CRUD_UI.modalOverlay}>
          <div className={`${CRUD_UI.modalContainer} max-w-xl`} onClick={(event) => event.stopPropagation()}>
            {modalType === 'profile' && (
              <>
                <div className={CRUD_UI.modalHeader}>
                  <h2 className={CRUD_UI.modalTitle}>Thông tin cá nhân</h2>
                  <button type="button" className={CRUD_UI.modalCloseButton} onClick={closeModal}>
                    <FiX className="text-xl" />
                  </button>
                </div>
                <form
                  className={`${CRUD_UI.modalForm} space-y-4`}
                  onSubmit={(event) => {
                    event.preventDefault();
                    handleSaveProfile();
                  }}
                >
                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <label className="mb-1.5 block text-xs font-medium text-slate-700">DisplayName</label>
                      <div className="flex items-center rounded border border-slate-300 bg-white px-3 py-2">
                        <FiUser className="mr-2 text-slate-400" />
                        <input
                          type="text"
                          value={profileForm.displayName}
                          onChange={(event) => {
                            const value = event.target.value;
                            setProfileForm((prev) => ({ ...prev, displayName: value }));
                            setProfileErrors((prev) => ({ ...prev, displayName: validateProfileField('displayName', value) || undefined }));
                          }}
                          onBlur={() => setProfileTouched((prev) => ({ ...prev, displayName: true }))}
                          className="w-full border-none bg-transparent text-sm text-slate-800 outline-none"
                          minLength={3}
                          maxLength={50}
                          required
                        />
                      </div>
                      {profileTouched.displayName && profileErrors.displayName && <p className="mt-1 text-xs text-red-600">{profileErrors.displayName}</p>}
                    </div>
                    <div>
                      <label className="mb-1.5 block text-xs font-medium text-slate-700">Username</label>
                      <div className="w-full rounded border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
                        {(user as any)?.username || '-'}
                      </div>
                    </div>
                    <div>
                      <label className="mb-1.5 block text-xs font-medium text-slate-700">Email</label>
                      <input
                        type="email"
                        value={profileForm.email}
                        onChange={(event) => {
                          const value = event.target.value;
                          setProfileForm((prev) => ({ ...prev, email: value }));
                          setProfileErrors((prev) => ({ ...prev, email: validateProfileField('email', value) || undefined }));
                        }}
                        onBlur={() => setProfileTouched((prev) => ({ ...prev, email: true }))}
                        className={`w-full rounded border bg-white px-3 py-2 text-sm text-slate-800 focus:outline-none focus:border-primary ${profileTouched.email && profileErrors.email ? 'border-red-500' : 'border-slate-300'}`}
                        required
                      />
                      {profileTouched.email && profileErrors.email && <p className="mt-1 text-xs text-red-600">{profileErrors.email}</p>}
                    </div>
                  </div>
                  <div className={CRUD_UI.modalFooter}>
                    <button type="button" onClick={closeModal} className={CRUD_UI.secondaryButton}>Hủy</button>
                    <button type="submit" disabled={saving} className={CRUD_UI.primaryButton}>
                      <FiSave className="text-base" />
                      {saving ? 'Đang lưu...' : 'Lưu'}
                    </button>
                  </div>
                </form>
              </>
            )}

            {modalType === 'security' && (
              <>
                <div className={CRUD_UI.modalHeader}>
                  <h2 className={CRUD_UI.modalTitle}>Đổi mật khẩu</h2>
                  <button type="button" className={CRUD_UI.modalCloseButton} onClick={closeModal}>
                    <FiX className="text-xl" />
                  </button>
                </div>
                <form
                  className={`${CRUD_UI.modalForm} space-y-4`}
                  onSubmit={(event) => {
                    event.preventDefault();
                    handleChangePassword();
                  }}
                >
                  <div>
                    <label className="mb-1.5 block text-xs font-medium text-slate-700">Mật khẩu hiện tại</label>
                    <div className="relative">
                      <input
                        type={showCurrentPassword ? "text" : "password"}
                        value={securityForm.currentPassword}
                        onChange={(event) => {
                          const value = event.target.value;
                          setSecurityForm((prev) => ({ ...prev, currentPassword: value }));
                          setSecurityErrors((prev) => ({ ...prev, currentPassword: validateSecurityField('currentPassword', value) || undefined }));
                        }}
                        onBlur={() => setSecurityTouched((prev) => ({ ...prev, currentPassword: true }))}
                        className={`w-full rounded border bg-white px-3 py-2 pr-10 text-sm text-slate-800 focus:outline-none focus:border-primary ${securityTouched.currentPassword && securityErrors.currentPassword ? 'border-red-500' : 'border-slate-300'}`}
                        required
                      />
                      <button
                        type="button"
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      >
                        {showCurrentPassword ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                      </button>
                    </div>
                    {securityTouched.currentPassword && securityErrors.currentPassword && <p className="mt-1 text-xs text-red-600">{securityErrors.currentPassword}</p>}
                  </div>
                  <div>
                    <label className="mb-1.5 block text-xs font-medium text-slate-700">Mật khẩu mới</label>
                    <div className="relative">
                      <input
                        type={showNewPassword ? "text" : "password"}
                        value={securityForm.newPassword}
                        onChange={(event) => {
                          const value = event.target.value;
                          setSecurityForm((prev) => ({ ...prev, newPassword: value }));
                          setSecurityErrors((prev) => ({
                            ...prev,
                            newPassword: validateSecurityField('newPassword', value) || undefined,
                            confirmPassword: validateSecurityField('confirmPassword', securityForm.confirmPassword, { ...securityForm, newPassword: value }) || undefined,
                          }));
                        }}
                        onBlur={() => setSecurityTouched((prev) => ({ ...prev, newPassword: true }))}
                        className={`w-full rounded border bg-white px-3 py-2 pr-10 text-sm text-slate-800 focus:outline-none focus:border-primary ${securityTouched.newPassword && securityErrors.newPassword ? 'border-red-500' : 'border-slate-300'}`}
                        minLength={6}
                        required
                      />
                      <button
                        type="button"
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                      >
                        {showNewPassword ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                      </button>
                    </div>
                    {securityTouched.newPassword && securityErrors.newPassword && <p className="mt-1 text-xs text-red-600">{securityErrors.newPassword}</p>}
                  </div>
                  <div>
                    <label className="mb-1.5 block text-xs font-medium text-slate-700">Xác nhận mật khẩu mới</label>
                    <div className="relative">
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        value={securityForm.confirmPassword}
                        onChange={(event) => {
                          const value = event.target.value;
                          setSecurityForm((prev) => ({ ...prev, confirmPassword: value }));
                          setSecurityErrors((prev) => ({
                            ...prev,
                            confirmPassword: validateSecurityField('confirmPassword', value, { ...securityForm, confirmPassword: value }) || undefined
                          }));
                        }}
                        onBlur={() => setSecurityTouched((prev) => ({ ...prev, confirmPassword: true }))}
                        className={`w-full rounded border bg-white px-3 py-2 pr-10 text-sm text-slate-800 focus:outline-none focus:border-primary ${securityTouched.confirmPassword && securityErrors.confirmPassword ? 'border-red-500' : 'border-slate-300'}`}
                        minLength={6}
                        required
                      />
                      <button
                        type="button"
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      >
                        {showConfirmPassword ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                      </button>
                    </div>
                    {securityTouched.confirmPassword && securityErrors.confirmPassword && <p className="mt-1 text-xs text-red-600">{securityErrors.confirmPassword}</p>}
                  </div>
                  <div className={CRUD_UI.modalFooter}>
                    <button type="button" onClick={closeModal} className={CRUD_UI.secondaryButton}>Hủy</button>
                    <button type="submit" disabled={saving} className={CRUD_UI.primaryButton}>
                      <FiKey className="text-base" />
                      {saving ? 'Đang cập nhật...' : 'Cập nhật'}
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export default UserProfileMenu;